import React, { useEffect, useRef, useState } from 'react';

import CurrencySymbol from '@/components/CurrencySymbol';
import { useInterval } from '@/hooks/useInterval';
import { CurrencyCodeType } from '@/types/currency';
import { JackpotBetAmountsType } from '@/types/jackpotTypes';
import {
  formatNumberToCurrency,
  isPrefixCurrencySymbol,
  mapCurrencyToSymbol,
} from '@/utils/currency';
import { isEqual } from '@/utils/lodash-alternatives';

import AnimatedDigit from './AnimatedDigit';
import styles from './JackpotBetWidget.module.scss';
import JackpotBetWidgetType from './JackpotBetWidgetType';

export interface JackpotBetWidgetHeaderPropTypes {
  winnersText: string;
  winnersToday: number;
  winsText: string;
  winsToday: number;
  logo: {
    url: string;
  };
  currencyCode?: CurrencyCodeType;
  amounts: JackpotBetAmountsType[];
  prizeLabels: Record<string, string>;
}

const JackpotBetWidgetHeader: React.FC<JackpotBetWidgetHeaderPropTypes> = React.memo(
  ({
    winnersText,
    winnersToday,
    winsText,
    winsToday,
    logo,
    currencyCode,
    amounts,
    prizeLabels,
  }: JackpotBetWidgetHeaderPropTypes) => {
    const isInitialRender = useRef(true);
    const [isActiveInitialAnimation, setIsActiveInitialAnimation] = useState(
      false
    );
    const [isActiveMainAnimation, setIsActiveMainAnimation] = useState(false);

    const [winnersInitialValueToDisplay, setWinnersValueInit] = useState('');
    const [winnersValueToDisplay, setWinnersValue] = useState(winnersToday);

    const [winsInitialValueToDisplay, setWinsInitialValue] = useState('');
    const [winsValueToDisplay, setWinsValue] = useState(winsToday);

    const [initialWinnersAnimationIndexPos, setIndexPos] = useState(0);
    const [
      initialWinsAnimationIndexPos,
      setInitialWinsAnimationIndexPos,
    ] = useState(0);

    const [
      isInitialWinnersAnimationIntervalRunning,
      setIsInitialWinnersAnimationIntervalRunning,
    ] = useState(false);
    const [
      isInitialWinsAnimationIntervalRunning,
      setIsInitialWinsAnimationIntervalRunning,
    ] = useState(false);

    const winnersValueArr = winnersToday.toLocaleString().split('');
    const winsValueArr = formatNumberToCurrency(
      winsValueToDisplay,
      currencyCode
    ).split('');

    const currencySymbol = mapCurrencyToSymbol(currencyCode);
    const isPrefix = isPrefixCurrencySymbol(currencyCode);

    const getAnimatedDigits = (numberAsString: string = '') =>
      numberAsString.split('').map((digit, index) => {
        const decimalPointPos = numberAsString.indexOf('.');
        const isDecimalValue = decimalPointPos > 0 && index > decimalPointPos;

        return (
          <AnimatedDigit
            key={index}
            value={digit}
            isDecimalValue={isDecimalValue}
          />
        );
      });

    // Initial winners animation
    useInterval(
      () => {
        if (initialWinnersAnimationIndexPos <= winnersValueArr.length - 1) {
          const nextVal = winnersValueArr[initialWinnersAnimationIndexPos];

          setWinnersValueInit(prevVal => `${prevVal}${nextVal}`);
          setIndexPos(prevAnimatedIndexPos => prevAnimatedIndexPos + 1);
        }
        if (initialWinnersAnimationIndexPos === winnersValueArr.length) {
          setIsInitialWinnersAnimationIntervalRunning(false);
        }
      },
      isInitialWinnersAnimationIntervalRunning ? 50 : null
    );

    // Initial wins animation
    useInterval(
      () => {
        if (initialWinsAnimationIndexPos <= winsValueArr.length - 1) {
          const nextVal = winsValueArr[initialWinsAnimationIndexPos];

          setWinsInitialValue(prevVal => `${prevVal}${nextVal}`);
          setInitialWinsAnimationIndexPos(
            prevAnimatedIndexPos => prevAnimatedIndexPos + 1
          );
        }
        if (initialWinsAnimationIndexPos === winsValueArr.length) {
          setIsInitialWinsAnimationIntervalRunning(false);
          setIsActiveInitialAnimation(false);
          setIsActiveMainAnimation(true);
        }
      },
      isInitialWinsAnimationIntervalRunning ? 50 : null
    );

    useEffect(() => {
      if (isInitialRender.current) {
        if (!winnersToday || !winsToday) return;

        // Run the initial animation only once
        isInitialRender.current = false;

        const int = setTimeout(() => {
          // Display initial animation
          setIsActiveInitialAnimation(true);

          // Run initial animation of winners
          setIsInitialWinnersAnimationIntervalRunning(true);

          // Run initial animation of wins
          setIsInitialWinsAnimationIntervalRunning(true);
        }, 800);
        return () => clearTimeout(int);
      } else {
        // After each update of wins or winners data animate the change of value
        setWinnersValue(winnersToday);
        setWinsValue(winsToday);
      }
    }, [winnersToday, winsToday]);

    return (
      <div className={styles.header} data-testid="jackpot-bet-header">
        <img src={logo.url} className={styles.logo} alt="Jackpot Bet Logo" />
        <div className={styles.badges} data-testid="jackpot-bet-badges">
          <div className={styles.badgeRow}>
            <div className={styles.badge}>
              <div className={styles.badgeText}>{winnersText}</div>
              <div className={styles.badgeSide} />
              <div
                className={styles.badgeValue}
                data-testid="jackpot-bet-badge-value-winners"
              >
                <p>
                  {isActiveInitialAnimation
                    ? getAnimatedDigits(winnersInitialValueToDisplay)
                    : isActiveMainAnimation
                    ? ''
                    : '0'}
                  {isActiveMainAnimation
                    ? getAnimatedDigits(winnersValueToDisplay.toLocaleString())
                    : ''}
                </p>
              </div>
            </div>
            <div className={styles.badge}>
              <div className={styles.badgeText}>{winsText}</div>
              <div className={styles.badgeSide} />
              <div
                className={styles.badgeValue}
                data-testid="jackpot-bet-badge-value-wins"
              >
                <p>
                  <CurrencySymbol
                    isPrefixElementPosition
                    isPrefixCurrencySymbol={isPrefix}
                    currencySymbol={currencySymbol}
                    elementTestId={'jackpot-bet-currency'}
                  />
                  {isActiveInitialAnimation
                    ? getAnimatedDigits(winsInitialValueToDisplay)
                    : isActiveMainAnimation
                    ? ''
                    : getAnimatedDigits('0.00')}
                  {isActiveMainAnimation
                    ? getAnimatedDigits(
                        formatNumberToCurrency(winsValueToDisplay, currencyCode)
                      )
                    : ''}
                  <CurrencySymbol
                    isPrefixElementPosition={false}
                    isPrefixCurrencySymbol={isPrefix}
                    currencySymbol={currencySymbol}
                    elementTestId={'jackpot-bet-currency'}
                  />
                </p>
              </div>
            </div>
          </div>
          <div className={styles.desktopType}>
            <JackpotBetWidgetType
              amounts={amounts}
              prizeLabels={prizeLabels}
              currencyCode={currencyCode}
            />
          </div>
        </div>
      </div>
    );
  },
  (
    prevProps: JackpotBetWidgetHeaderPropTypes,
    nextProps: JackpotBetWidgetHeaderPropTypes
  ) => isEqual(prevProps, nextProps)
);

export default JackpotBetWidgetHeader;
